/**
 * Domain value types and error codes (contracts.md).
 * No I/O, no dependencies on application or adapters.
 */

export type FieldId = string;

export type SceneId = "menu" | "map" | "game" | "settings" | "editor" | "story";

export interface Cell {
  row: number;
  col: number;
}

export const BuildingType = {
  Tower: "Tower",
  Wall: "Wall",
  Amplifier: "Amplifier",
  Trap: "Trap",
  Shrine: "Shrine",
} as const;
export type BuildingType = (typeof BuildingType)[keyof typeof BuildingType];

export const MonsterType = {
  Normal: "Normal",
  Swarm: "Swarm",
  Armored: "Armored",
  Giant: "Giant",
  Runner: "Runner",
} as const;
export type MonsterType = (typeof MonsterType)[keyof typeof MonsterType];

export const GemType = {
  Yellow: "Yellow",
  Orange: "Orange",
  Red: "Red",
  Purple: "Purple",
  Green: "Green",
  Blue: "Blue",
  Cyan: "Cyan",
  Lime: "Lime",
} as const;
export type GemType = (typeof GemType)[keyof typeof GemType];

/** Sound identifiers for AudioPort (contracts.md). */
export type SoundId =
  | "build"
  | "shot"
  | "hit"
  | "monsterDeath"
  | "orbHit"
  | "waveStart"
  | "win"
  | "lose"
  | "click"
  | "start";

/** Use case / domain error codes (contracts.md). */
export type ValidationError = { code: "ValidationError"; reason?: string };
export type NotEnoughMana = { code: "NotEnoughMana" };
export type InvalidCell = { code: "InvalidCell" };
export type BuildingLimitReached = { code: "BuildingLimitReached" };
export type InvalidSlot = { code: "InvalidSlot" };
export type AlreadyMaxLevel = { code: "AlreadyMaxLevel" };

export type PathBlocked = { code: "PathBlocked" };
export type CellOccupiedByMonster = { code: "CellOccupiedByMonster" };
export type CellBlocked = { code: "CellBlocked" };

export type PlaceBuildingError =
  | NotEnoughMana
  | InvalidCell
  | BuildingLimitReached
  | PathBlocked
  | CellOccupiedByMonster
  | CellBlocked;
export type PlaceGemError = NotEnoughMana | InvalidSlot | InvalidCell;
export type UpgradeGemError = NotEnoughMana | InvalidSlot | AlreadyMaxLevel;
export type DuplicateGemError = NotEnoughMana | InvalidSlot;
export type RecycleGemError = InvalidSlot;

/**
 * Errors returned by combineGems (session.ts, commit 2).
 * - SameGem: both ids point to the same gem.
 * - NotFound: at least one gem id absent from inventory.
 * - AlreadyMaxLevel: case 1/2 would push level > MAX_GEM_LEVEL.
 * - AlreadyDual: either input is already dual-type (MVP rejects triples).
 */
export type CombineGemError =
  | { code: "SameGem" }
  | { code: "NotFound" }
  | { code: "AlreadyMaxLevel" }
  | { code: "AlreadyDual" };

/** Field configuration DTO (contracts.md): grid size, path, and wave params. */
export interface FieldConfig {
  rows: number;
  cols: number;
  /**
   * @deprecated Phase D: use entryCells/exitCell/walkableCells instead.
   * Kept for backward-compat in tests; will be removed in a future cleanup.
   * If present, used as fallback source for entryCells/exitCell/walkableCells in createSession.
   */
  waypoints?: Cell[];
  /** Obstacle cells: impassable to monsters AND unbuildable. Empty array = open field (legacy). */
  blockedCells?: Cell[];
  /** Pre-placed buildings spawned at session start (tutorial helpers). Not counted as player-built. */
  prebuiltBuildings?: Array<{ cell: Cell; type: BuildingType }>;
  /** Number of waves (AS3: 8–80 per field). */
  wavesNum: number;
  /** Monsters per wave (AS3: 8–16 normal). */
  monstersPerWave: number;
  /** Interval between spawns within a wave (ms). */
  spawnIntervalMs: number;
  /** Base HP for monsters in wave 0. */
  monsterBaseHp: number;
  /** Armor growth rate per wave (default 0.11). */
  armorGrowthRate?: number;
  /** Delay in ms before first wave first spawn; default 0. */
  firstWaveDelayMs?: number;
  /** Pause in ms between end of one wave and start of the next. Default 0 (AS3: no pause). */
  interWaveDelayMs?: number;
  /** Walkable cells (monster path). Required for all new configs. */
  walkableCells?: Cell[];
  /** Entry points (monsters spawn here). Required for all new configs. */
  entryCells?: Cell[];
  /** Single exit (orb). Required for all new configs. */
  exitCell?: Cell;
  /** Override for initial mana (Свет) for this field. */
  initialMana?: number;
  /** Whitelist of gem types available on this field. Undefined = all types allowed. */
  allowedGemTypes?: GemType[];
}
